import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { deriveClientKey, loginKey } from "@vocifuchs/shared";
import { api, ApiError } from "../lib/api.js";
import { auth } from "../copy/auth.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { AuthLayout } from "../components/AuthLayout.js";
import { Banner, Button, Field, inputClass } from "../components/ui.js";
import { useSession } from "../session.js";

export function LoginScreen() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Whatever they typed -- shouted, spaced out, hyphenated -- becomes the
      // one key the account is stored under.
      const handle = loginKey(name);
      const dk = await deriveClientKey(pin, handle);
      await api("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ handle, dk }),
      });
      await refresh();
      navigate("/");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={auth.login.title}
      intro={auth.login.intro}
      footer={
        <div className="space-y-2 text-center text-sm lg:text-left">
          <p>
            <Link to="/pin/recover" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
              {auth.login.forgotPin}
            </Link>
          </p>
          <p>
            <Link to="/pin/reset" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
              {auth.login.teacherCode}
            </Link>
          </p>
          <p>
            <Link to="/register" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
              {auth.login.noAccount}
            </Link>
          </p>
        </div>
      }
    >
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error ? <Banner>{error}</Banner> : null}
        <Field label={auth.login.yourName}>
          <input
            className={inputClass()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="TanzendesBlauesPferd"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </Field>
        <Field label={auth.login.pin}>
          <input
            className={inputClass()}
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="current-password"
            required
          />
        </Field>
        <Button type="submit" disabled={busy}>
          {busy ? common.loading : auth.login.submit}
        </Button>
      </form>
    </AuthLayout>
  );
}
